/**
 * WHAT SAVING A CARD OR A BANK ACCOUNT MEANS HERE (ops #46, step 3), in the business's own words, in
 * the places a client meets them, each with words of its own since the review of the staging walk of
 * 2026-09-23. Each says what Stripe's compliance section asks of a saved method: that the business
 * will charge it, when, how the amount is set, and how to stop. The owner's rules stand in every one:
 * silence never charges, so a charge follows her word that the visit went well or the office having
 * checked with her; the amount is the published rate and nothing else (CLAUDE.md binding rule 3).
 *
 * - `AUTOPAY_TERMS`, beneath the pay button of the Stripe pages that offer Stripe's own save box, the
 *   card page and the office's page, where the box saves a card: ticking it is the opt-in.
 * - `AUTOPAY_BANK_TERMS`, beside her own tick on ops's bank screen, which saves the bank account she
 *   pays the invoice from: ticking it is the opt-in, and this is what she agrees to.
 * - `AUTOPAY_BANK_CONFIRMATION`, beneath the pay button of Stripe's bank page made for that tick, which
 *   draws no box of its own: two sentences confirming what she chose, what is charged and when, and how
 *   to stop. The whole paragraph ran to eleven lines there on a phone, and began "If you save your
 *   payment method here" after she had already chosen on ops's own screen.
 * - `AUTOPAY_CARD_TERMS` and `AUTOPAY_CARD_CONFIRMATION` (ops #70, 2026-10-17), the same two for home's
 *   own tick on its card screen, which saves the card she pays with: Link stood in for Stripe's box on
 *   the owner's card page, so the card has a tick of its own, as the bank has. The owner approved both
 *   word for word; they are the bank's with "card" for "bank account".
 * - `AUTOPAY_SETUP_BANK_TERMS` and `AUTOPAY_SETUP_CARD_TERMS` (ops #76, `billing.autopay_start`,
 *   approved by the owner word for word on 2026-10-17), beside home's button that saves a bank account
 *   or a card with nothing owed: pressing it is the opt-in, and they say that nothing is charged today.
 *   `AUTOPAY_SETUP_BANK_CONFIRMATION` and `AUTOPAY_SETUP_CARD_CONFIRMATION` go beneath the Save button
 *   of Stripe's setup page it makes.
 *
 * Where she agrees, the whole of it: what is not charged while something is not right, and that
 * nothing else is ever charged without asking her. How to stop, everywhere, names since ops #76 the
 * door she holds at home beside the phone and the email (the owner's answer 2 of 2026-10-17).
 *
 * SPELLED ONCE, HERE. The business phone is `BUSINESS_PHONE_DISPLAY`, never typed again, and
 * `api/test/autopay.test.ts` fails the suite on any other file that spells these words. They reach
 * Stripe as `custom_text[submit][message]`, which takes up to 1200 characters, and the same test holds
 * each under that.
 *
 * VERSIONED BY THEIR CONTENT. Every authorization row keeps the version of the words she agreed to, the
 * hex SHA-256 of them: `AUTOPAY_TERMS_VERSION` for a card saved in Stripe's box, and
 * `AUTOPAY_BANK_TERMS_VERSION` for an account saved by her tick, `AUTOPAY_CARD_TERMS_VERSION` for a card
 * saved by her tick on the card screen, and `AUTOPAY_SETUP_BANK_TERMS_VERSION` or
 * `AUTOPAY_SETUP_CARD_TERMS_VERSION` for a method saved from home's button, so the row says exactly
 * which words she read. Each hash is pinned rather than computed at load, because computing it needs an
 * async digest; the same test computes each from its text and fails until a changed text's new hash is
 * pinned here, so the words and the version never drift apart, the confirmations' included.
 *
 * AND EVERY EARLIER VERSION KEPT (ops #54, item 2; ops #76). New words are a new version, and a row
 * recorded under earlier words keeps the version it was written with, since nothing rewrites
 * `consent_text_version`. So each version a row can hold stays here beside the words it names
 * (`AUTOPAY_WORDS_VERSIONS`, `autopayWordsOf`), frozen as they were shown, and the same test holds
 * each earlier version to its words as it holds the current ones. The sentences beneath Stripe's
 * button are kept the same way, apart, since no row keeps theirs (`AUTOPAY_CONFIRMATION_VERSIONS`).
 */
import { BUSINESS_PHONE_DISPLAY } from './enums.ts';

/** What is charged, when, and at what: the same in every place. */
const WHAT_AND_WHEN =
  "for each clean's invoice after you tell us the visit went well, or once we have checked " +
  'with you that it did, at the rate published for your home on faithfullykept.com';

/** What is not charged, and nothing else ever: where she agrees. */
const NOTHING_ELSE =
  'If you tell us something was not right, nothing is charged until it is put right. ' +
  'We never charge anything else without asking you first.';

/**
 * WHEN A RECEIPT GOES, where she agrees (ops #54, item 2): the charge asks Stripe for one only where
 * the household's payer has an email the register lets ops write to (`receiptGoesByEmail`), so the
 * words promise one only there, and never to a payer with no email.
 */
const RECEIPT =
  'Each time, we email a receipt if we have an email address for you, unless you have asked us not to.';

/**
 * How to stop: the same in every place, and since ops #76 (the owner's answer 2 of 2026-10-17) with
 * the door she holds at home, Stop on her account page, beside the phone and the email. Exported for
 * the receipt of an automatic charge (`emails.ts`, E2), which ends with it too: spelled here alone.
 */
export const HOW_TO_STOP =
  `You can stop this at any time by calling or texting ${BUSINESS_PHONE_DISPLAY}, emailing ` +
  'hello@faithfullykept.com, or with Stop on your account page at home.faithfullykept.com.';

/** A setup page takes no money, so the words where she saves a method on one say so. */
const NOTHING_TODAY = 'Nothing is charged today.';

/** Beneath the pay button of a Stripe page that offers Stripe's own save box, which saves a card. */
export const AUTOPAY_TERMS =
  `If you save your card here, Faithfully Kept will charge it ${WHAT_AND_WHEN}. ` +
  `${RECEIPT} ${NOTHING_ELSE} ${HOW_TO_STOP}`;

/** The hex SHA-256 of `AUTOPAY_TERMS`, as UTF-8. Pinned, and held to the text by a test. */
export const AUTOPAY_TERMS_VERSION =
  '2c9d9de7f49af187738e0cf56e1b8af6b6a2d0aef2a8109fbd27df3e39d6d00b';

/** Beside her own tick on ops's bank screen, "Charge my bank account automatically after each clean". */
export const AUTOPAY_BANK_TERMS =
  'If you tick this, the bank account you pay this invoice from is saved, and Faithfully Kept ' +
  `will charge it ${WHAT_AND_WHEN}. ${RECEIPT} ${NOTHING_ELSE} ${HOW_TO_STOP}`;

/** The hex SHA-256 of `AUTOPAY_BANK_TERMS`, as UTF-8. Pinned, and held to the text by a test. */
export const AUTOPAY_BANK_TERMS_VERSION =
  '71e63de6c115f33dba243e90665f53831eed41d19cacf23832d76340a5184844';

/** Beneath the pay button of Stripe's bank page made for her tick: what she chose, in two sentences. */
export const AUTOPAY_BANK_CONFIRMATION =
  'You chose to save this bank account, so Faithfully Kept will charge it ' +
  `${WHAT_AND_WHEN}. ${HOW_TO_STOP}`;

/** The hex SHA-256 of `AUTOPAY_BANK_CONFIRMATION`, as UTF-8. Pinned, and held to the text by a test. */
export const AUTOPAY_BANK_CONFIRMATION_VERSION =
  '5c4f8d5eb97dfaabf430ccc836505b1a23233fdf47fe36a24188df0398b49134';

/**
 * Beside home's own tick on the card screen, "Charge my card automatically after each clean" (ops #70,
 * approved by the owner word for word on 2026-10-17): the bank's, with "card" for "bank account".
 */
export const AUTOPAY_CARD_TERMS =
  'If you tick this, the card you pay this invoice with is saved, and Faithfully Kept will ' +
  `charge it ${WHAT_AND_WHEN}. ${RECEIPT} ${NOTHING_ELSE} ${HOW_TO_STOP}`;

/** The hex SHA-256 of `AUTOPAY_CARD_TERMS`, as UTF-8. Pinned, and held to the text by a test. */
export const AUTOPAY_CARD_TERMS_VERSION =
  '426159e422cdd93849af765d30717547ea5598704cd62f3e7e230513739f1314';

/**
 * Beneath the pay button of Stripe's card page made for her tick (ops #70, approved by the owner word
 * for word on 2026-10-17): what she chose, in two sentences, as on the bank page.
 */
export const AUTOPAY_CARD_CONFIRMATION =
  'You chose to save this card, so Faithfully Kept will charge it ' +
  `${WHAT_AND_WHEN}. ${HOW_TO_STOP}`;

/** The hex SHA-256 of `AUTOPAY_CARD_CONFIRMATION`, as UTF-8. Pinned, and held to the text by a test. */
export const AUTOPAY_CARD_CONFIRMATION_VERSION =
  '51b57f07f8827a5449d113705653833ac9d81f489702ab202e943c8f9c7da3bf';

/**
 * Beside home's button that saves a bank account with nothing owed (ops #76, `billing.autopay_start`,
 * approved by the owner word for word on 2026-10-17): pressing it is the opt-in, the whole of it.
 */
export const AUTOPAY_SETUP_BANK_TERMS =
  'If you save a bank account here, Faithfully Kept will charge it ' +
  `${WHAT_AND_WHEN}. ${NOTHING_TODAY} ${RECEIPT} ${NOTHING_ELSE} ${HOW_TO_STOP}`;

/** The hex SHA-256 of `AUTOPAY_SETUP_BANK_TERMS`, as UTF-8. Pinned, and held to the text by a test. */
export const AUTOPAY_SETUP_BANK_TERMS_VERSION =
  'e59f75fc3ff1770285014c494cde3a79cfe9e3e896b4e02611e891d0b8c359fb';

/** Beside home's button that saves a card with nothing owed: the bank's, with "a card". */
export const AUTOPAY_SETUP_CARD_TERMS =
  'If you save a card here, Faithfully Kept will charge it ' +
  `${WHAT_AND_WHEN}. ${NOTHING_TODAY} ${RECEIPT} ${NOTHING_ELSE} ${HOW_TO_STOP}`;

/** The hex SHA-256 of `AUTOPAY_SETUP_CARD_TERMS`, as UTF-8. Pinned, and held to the text by a test. */
export const AUTOPAY_SETUP_CARD_TERMS_VERSION =
  '7a4ea6023fe323be31c4eeadbaea6f660ff0c002cea1406961aff75d86b10a73';

/**
 * Beneath the Save button of Stripe's setup page for a bank account (ops #76, approved by the owner
 * word for word on 2026-10-17): what she chose, that nothing is charged today, and how to stop.
 */
export const AUTOPAY_SETUP_BANK_CONFIRMATION =
  'You chose to save this bank account, so Faithfully Kept will charge it ' +
  `${WHAT_AND_WHEN}. ${NOTHING_TODAY} ${HOW_TO_STOP}`;

/** The hex SHA-256 of `AUTOPAY_SETUP_BANK_CONFIRMATION`, as UTF-8. Pinned, and held by a test. */
export const AUTOPAY_SETUP_BANK_CONFIRMATION_VERSION =
  '5773cc3f742e11367fd5996e3e8e77677e95ee79840563c2d180f75f3601b904';

/** Beneath the Save button of Stripe's setup page for a card: the bank's, with "this card". */
export const AUTOPAY_SETUP_CARD_CONFIRMATION =
  'You chose to save this card, so Faithfully Kept will charge it ' +
  `${WHAT_AND_WHEN}. ${NOTHING_TODAY} ${HOW_TO_STOP}`;

/** The hex SHA-256 of `AUTOPAY_SETUP_CARD_CONFIRMATION`, as UTF-8. Pinned, and held by a test. */
export const AUTOPAY_SETUP_CARD_CONFIRMATION_VERSION =
  '6b5293a1d3b4ceab79be79841575f84a1fde84495eccc2fcafbe36b6e9bca0b5';

/**
 * WHERE SHE MET THE WORDS A VERSION NAMES, which `household.autopay.agreed_through` says
 * (`docs/seams/home.md` section 12.1): beneath Stripe's save box, beside her own tick on the bank
 * screen, beside home's tick on the card screen (ops #70), or beside home's button that saves a bank
 * account or a card with nothing owed (ops #76).
 */
export const AUTOPAY_WORDS_PLACES = Object.freeze([
  'stripe_box',
  'bank_tick',
  'card_tick',
  'setup_bank',
  'setup_card',
] as const);
export type AutopayWordsPlace = (typeof AUTOPAY_WORDS_PLACES)[number];

/** One version of the words of a place: its hash, the place, and the words. */
export interface AutopayWordsVersion {
  readonly version: string;
  readonly place: AutopayWordsPlace;
  readonly words: string;
}

/**
 * The words of 2026-09-22, beneath the pay button of every Stripe page that offered Stripe's own
 * save box, the bank page among them then. Frozen as they were shown.
 */
const WORDS_OF_2026_09_22 =
  "If you save your payment method here, Faithfully Kept will charge it for each clean's invoice " +
  'after you tell us the visit went well, or once we have checked with you that it did, at the ' +
  'rate published for your home on faithfullykept.com, and will email you a receipt each time. If ' +
  'you tell us something was not right, nothing is charged until it is put right. We never charge ' +
  'anything else without asking you first. You can stop this at any time by calling or texting ' +
  `${BUSINESS_PHONE_DISPLAY} or emailing hello@faithfullykept.com.`;

/**
 * The words of 2026-09-23 beneath Stripe's save box, which promised a receipt each time whoever
 * paid (ops #54, item 2). Frozen as they were shown.
 */
const CARD_WORDS_OF_2026_09_23 =
  "If you save your card here, Faithfully Kept will charge it for each clean's invoice after you " +
  'tell us the visit went well, or once we have checked with you that it did, at the rate ' +
  'published for your home on faithfullykept.com, and will email you a receipt each time. If you ' +
  'tell us something was not right, nothing is charged until it is put right. We never charge ' +
  'anything else without asking you first. You can stop this at any time by calling or texting ' +
  `${BUSINESS_PHONE_DISPLAY} or emailing hello@faithfullykept.com.`;

/**
 * The words of 2026-09-23 beside her own tick on the bank screen, which promised the same (ops #54,
 * item 2). Frozen as they were shown.
 */
const BANK_WORDS_OF_2026_09_23 =
  'If you tick this, the bank account you pay this invoice from is saved, and Faithfully Kept will ' +
  "charge it for each clean's invoice after you tell us the visit went well, or once we have " +
  'checked with you that it did, at the rate published for your home on faithfullykept.com, and ' +
  'will email you a receipt each time. If you tell us something was not right, nothing is charged ' +
  'until it is put right. We never charge anything else without asking you first. You can stop ' +
  `this at any time by calling or texting ${BUSINESS_PHONE_DISPLAY} or emailing hello@faithfullykept.com.`;

/**
 * The words of ops #54 (2026-09-28) beneath Stripe's save box, a receipt promised only where one
 * goes, which ended before the door she holds at home was named (ops #76). Frozen as they were shown.
 */
const BOX_WORDS_OF_OPS_54 =
  "If you save your card here, Faithfully Kept will charge it for each clean's invoice after you " +
  'tell us the visit went well, or once we have checked with you that it did, at the rate ' +
  'published for your home on faithfullykept.com. Each time, we email a receipt if we have an ' +
  'email address for you, unless you have asked us not to. If you tell us something was not right, ' +
  'nothing is charged until it is put right. We never charge anything else without asking you ' +
  'first. You can stop this at any time by calling or texting ' +
  `${BUSINESS_PHONE_DISPLAY} or emailing hello@faithfullykept.com.`;

/**
 * The words of ops #54 (2026-09-28) beside her own tick on the bank screen, which ended the same
 * way. Frozen as they were shown.
 */
const BANK_WORDS_OF_OPS_54 =
  'If you tick this, the bank account you pay this invoice from is saved, and Faithfully Kept will ' +
  "charge it for each clean's invoice after you tell us the visit went well, or once we have " +
  'checked with you that it did, at the rate published for your home on faithfullykept.com. Each ' +
  'time, we email a receipt if we have an email address for you, unless you have asked us not to. ' +
  'If you tell us something was not right, nothing is charged until it is put right. We never ' +
  'charge anything else without asking you first. You can stop this at any time by calling or ' +
  `texting ${BUSINESS_PHONE_DISPLAY} or emailing hello@faithfullykept.com.`;

/**
 * The words of ops #70 (2026-10-17) beside home's tick on the card screen, as the owner approved them
 * there, which ended the same way. Frozen as they were shown.
 */
const CARD_TICK_WORDS_OF_OPS_70 =
  'If you tick this, the card you pay this invoice with is saved, and Faithfully Kept will charge ' +
  "it for each clean's invoice after you tell us the visit went well, or once we have checked with " +
  'you that it did, at the rate published for your home on faithfullykept.com. Each time, we email ' +
  'a receipt if we have an email address for you, unless you have asked us not to. If you tell us ' +
  'something was not right, nothing is charged until it is put right. We never charge anything ' +
  'else without asking you first. You can stop this at any time by calling or texting ' +
  `${BUSINESS_PHONE_DISPLAY} or emailing hello@faithfullykept.com.`;

/**
 * EVERY VERSION A ROW CAN HOLD, oldest first, the current five last: what `consent_text_version` on a
 * saved method names, the words it names, and where she met them. Each hash is pinned, and the test
 * that holds the current words to their versions holds these too.
 */
export const AUTOPAY_WORDS_VERSIONS: readonly AutopayWordsVersion[] = [
  {
    version: '1afd42e29be68c6d089359d697f2f6b1a321d652ccee4f8cca86df386dff77e4',
    place: 'stripe_box',
    words: WORDS_OF_2026_09_22,
  },
  {
    version: 'ca811acccfc445fc717a0cdd42817c72aac0dee3ad5826f1f2b327408ad2ed8f',
    place: 'stripe_box',
    words: CARD_WORDS_OF_2026_09_23,
  },
  {
    version: '8de73e0392ae8a20162bf1011a06d940ade4d76b68838a475bb4a93a67caa87c',
    place: 'bank_tick',
    words: BANK_WORDS_OF_2026_09_23,
  },
  {
    version: '32dacbba72ac64cc0cc1258855d1f76559b0b8dc72aa6f7b4d13c2a3dae4d673',
    place: 'stripe_box',
    words: BOX_WORDS_OF_OPS_54,
  },
  {
    version: '268827352361bfd744d9f017455d125dbbb3dee84f173f72e90baa77fe262830',
    place: 'bank_tick',
    words: BANK_WORDS_OF_OPS_54,
  },
  {
    version: '3006beeac497cb66975a6f3f69ded974c8d18abab0bedcc6794defde0a8d0cc2',
    place: 'card_tick',
    words: CARD_TICK_WORDS_OF_OPS_70,
  },
  { version: AUTOPAY_TERMS_VERSION, place: 'stripe_box', words: AUTOPAY_TERMS },
  { version: AUTOPAY_BANK_TERMS_VERSION, place: 'bank_tick', words: AUTOPAY_BANK_TERMS },
  { version: AUTOPAY_CARD_TERMS_VERSION, place: 'card_tick', words: AUTOPAY_CARD_TERMS },
  {
    version: AUTOPAY_SETUP_BANK_TERMS_VERSION,
    place: 'setup_bank',
    words: AUTOPAY_SETUP_BANK_TERMS,
  },
  {
    version: AUTOPAY_SETUP_CARD_TERMS_VERSION,
    place: 'setup_card',
    words: AUTOPAY_SETUP_CARD_TERMS,
  },
];

/** The words a saved method's `consent_text_version` names, or null for a version never shown. */
export function autopayWordsOf(version: string): string | null {
  return AUTOPAY_WORDS_VERSIONS.find((one) => one.version === version)?.words ?? null;
}

/**
 * The two sentences of 2026-09-23 beneath the pay button of Stripe's bank page made for her tick,
 * which ended before the door she holds at home was named (ops #76). Frozen as they were shown.
 */
const BANK_PAGE_WORDS_OF_2026_09_23 =
  "You chose to save this bank account, so Faithfully Kept will charge it for each clean's invoice " +
  'after you tell us the visit went well, or once we have checked with you that it did, at the ' +
  'rate published for your home on faithfullykept.com. You can stop this at any time by calling ' +
  `or texting ${BUSINESS_PHONE_DISPLAY} or emailing hello@faithfullykept.com.`;

/**
 * The two sentences of ops #70 (2026-10-17) beneath the pay button of Stripe's card page made for her
 * card tick, as the owner approved them there, which ended the same way. Frozen as they were shown.
 */
const CARD_PAGE_WORDS_OF_OPS_70 =
  "You chose to save this card, so Faithfully Kept will charge it for each clean's invoice after " +
  'you tell us the visit went well, or once we have checked with you that it did, at the rate ' +
  'published for your home on faithfullykept.com. You can stop this at any time by calling or ' +
  `texting ${BUSINESS_PHONE_DISPLAY} or emailing hello@faithfullykept.com.`;

/**
 * EVERY VERSION OF THE SENTENCES BENEATH STRIPE'S BUTTON, oldest first, the current four last, each
 * under the place whose page carries it (ops #76, the owner's answer 2: every earlier version kept
 * beside its words). Apart from `AUTOPAY_WORDS_VERSIONS`, because she agrees beside her tick or home's
 * button and not here, so no row keeps one of these and `agreed_through` never names Stripe's page.
 */
export const AUTOPAY_CONFIRMATION_VERSIONS: readonly AutopayWordsVersion[] = [
  {
    version: '666a86b0728ae4f9e158252925ca8b3e4ffcb20fe6985890d5131450d13f04b9',
    place: 'bank_tick',
    words: BANK_PAGE_WORDS_OF_2026_09_23,
  },
  {
    version: 'e66491cb8948f92f3e4a5e1050b22b31a23e0e93bd9e4e8934dd605be1703442',
    place: 'card_tick',
    words: CARD_PAGE_WORDS_OF_OPS_70,
  },
  {
    version: AUTOPAY_BANK_CONFIRMATION_VERSION,
    place: 'bank_tick',
    words: AUTOPAY_BANK_CONFIRMATION,
  },
  {
    version: AUTOPAY_CARD_CONFIRMATION_VERSION,
    place: 'card_tick',
    words: AUTOPAY_CARD_CONFIRMATION,
  },
  {
    version: AUTOPAY_SETUP_BANK_CONFIRMATION_VERSION,
    place: 'setup_bank',
    words: AUTOPAY_SETUP_BANK_CONFIRMATION,
  },
  {
    version: AUTOPAY_SETUP_CARD_CONFIRMATION_VERSION,
    place: 'setup_card',
    words: AUTOPAY_SETUP_CARD_CONFIRMATION,
  },
];
